// Add a user. Admin only, Pattern C at 640px.
//
// Its own route rather than an aside beside the table. A Franchisee sign-in
// can cover several clinics, so the clinic pick is a checkbox list rather
// than a dropdown.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createUser, listFranchisees } from '../api.js'
import { useApiForm } from '../useApiForm.js'
import { ROLES, ROLE_SCOPE } from '../../../shared/permissions.js'
import { Button, ButtonLink, Input, Notice, Panel, Select } from '../components/ui/index.js'

const EMPTY = { email: '', displayName: '', role: 'FRANCHISEE', franchiseeIds: [] }

export default function UserNew() {
  const navigate = useNavigate()
  const [form, setForm] = useState(EMPTY)
  const [franchisees, setFranchisees] = useState([])
  const [rosterError, setRosterError] = useState(null)
  const { saving, error, fieldErrors, submit } = useApiForm()

  useEffect(() => {
    listFranchisees()
      .then((data) => setFranchisees(data.franchisees))
      .catch((caught) => setRosterError(caught.message))
  }, [])

  function change(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  function toggleClinic(id) {
    setForm((current) => ({
      ...current,
      franchiseeIds: current.franchiseeIds.includes(id)
        ? current.franchiseeIds.filter((linked) => linked !== id)
        : [...current.franchiseeIds, id],
    }))
  }

  async function onSubmit(event) {
    event.preventDefault()
    const result = await submit(() =>
      createUser({
        email: form.email,
        displayName: form.displayName,
        role: form.role,
        // The server requires at least one for Franchisee and forbids any
        // otherwise.
        franchiseeIds: form.role === 'FRANCHISEE' ? form.franchiseeIds : [],
      }),
    )
    if (result) navigate('/admin/users')
  }

  return (
    <div className="form-page">
      <header className="page-head">
        <h1>Add a user</h1>
      </header>

      <Panel>
        <form onSubmit={onSubmit} noValidate>
          {error ? <Notice tone="bad">{error}</Notice> : null}
          {rosterError ? (
            <Notice tone="bad">The clinic list could not be loaded: {rosterError}</Notice>
          ) : null}

          <div className="formgrid">
            <Input
              id="email"
              className="span-2"
              label="Email"
              type="email"
              value={form.email}
              onChange={(event) => change('email', event.target.value)}
              error={fieldErrors.email}
              placeholder="name@fastestlabs.com"
            />

            <Input
              id="displayName"
              label="Name"
              type="text"
              value={form.displayName}
              onChange={(event) => change('displayName', event.target.value)}
              error={fieldErrors.displayName}
            />

            <Select
              id="role"
              label="Role"
              value={form.role}
              onChange={(role) => change('role', role)}
              help={ROLE_SCOPE[form.role]}
              options={ROLES.map((role) => ({ value: role.value, label: role.label }))}
            />
          </div>

          {form.role === 'FRANCHISEE' ? (
            <>
              <div className="form-section">
                <h2>Clinic names</h2>
                <p className="support">
                  Which clinics this sign-in covers. One Google account can run several.
                </p>
                {fieldErrors.franchiseeIds ? (
                  <p className="row-error">{fieldErrors.franchiseeIds}</p>
                ) : null}
              </div>
              <div className="rows">
                {franchisees.map((clinic) => (
                  <label key={clinic.id} className="row row-check">
                    <div className="row-main">
                      <div className="row-primary">{clinic.name}</div>
                      <div className="row-meta">{clinic.territory || 'No territory set'}</div>
                    </div>
                    <div className="row-trail">
                      <input
                        type="checkbox"
                        checked={form.franchiseeIds.includes(clinic.id)}
                        onChange={() => toggleClinic(clinic.id)}
                        aria-label={`Clinic: ${clinic.name}`}
                      />
                    </div>
                  </label>
                ))}
              </div>
            </>
          ) : null}

          {/* Primary action last on desktop. */}
          <div className="btnrow">
            <ButtonLink to="/admin/users">Cancel</ButtonLink>
            <span className="spacer" />
            <Button type="submit" variant="primary" block disabled={saving}>
              {saving ? 'Adding' : 'Add user'}
            </Button>
          </div>
        </form>
      </Panel>
    </div>
  )
}
